/**
 * BudgetsPage - List of forecast budgets
 *
 * Displays all budgets with filters, creation, and duplication modals.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBudgetStore } from '../store/budgetStore';
import { useBilanStore } from '../store/bilanStore';
import { useAccountStore } from '../store/accountStore';
import { useAuthStore } from '../store/authStore';
import { BudgetCard, BudgetFormModal, BudgetDuplicateModal } from '../components/budgets';
import { CreateBudgetRequest, DuplicateBudgetRequest, BudgetStatus } from '@budgira/shared';

const BudgetsPage = () => {
  const { t } = useTranslation();
  const {
    budgets,
    totalPages,
    isLoading,
    error,
    limit,
    isSubmitting,
    filters,
    page,
    fetchBudgets,
    createBudget,
    deleteBudget,
    duplicateBudget,
    checkLimit,
    setFilters,
    setPage,
    clearError,
  } = useBudgetStore();

  const { statements, fetchStatements } = useBilanStore();
  const { accounts, fetchAccounts } = useAccountStore();
  const canManageBudgets = useAuthStore((state) => state.canManageBudgets());

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [duplicateId, setDuplicateId] = useState<string | null>(null);

  // Fetch data on mount
  useEffect(() => {
    fetchBudgets();
    fetchAccounts();
    fetchStatements();
    checkLimit();
  }, [fetchBudgets, fetchAccounts, fetchStatements, checkLimit]);

  // Refetch when filters or page change
  useEffect(() => {
    fetchBudgets();
  }, [filters, page, fetchBudgets]);

  const handleCreate = async (data: CreateBudgetRequest) => {
    const result = await createBudget(data);
    if (result) {
      setIsFormOpen(false);
    }
  };

  const handleDelete = async (id: string) => {
    const success = await deleteBudget(id);
    if (success) {
      setDeleteConfirmId(null);
    }
  };

  const handleDuplicate = async (data: DuplicateBudgetRequest) => {
    if (!duplicateId) return;
    const result = await duplicateBudget(duplicateId, data);
    if (result) {
      setDuplicateId(null);
    }
  };

  const canCreate = limit?.canCreate ?? true;
  const activeAccounts = accounts.filter((a) => !a.isArchived);

  // Generate year options (last 10 years + next 2)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 12 }, (_, i) => currentYear - 9 + i);

  const duplicateBudgetData = budgets.find((b) => b.id === duplicateId);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            {t('budgets.title')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('budgets.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {limit && !canCreate && (
            <span className="text-sm text-amber-600 dark:text-amber-400">
              {t('budgets.limitReached', { count: limit.count, limit: limit.limit })}
            </span>
          )}
          {canManageBudgets && (
            <button
              onClick={() => setIsFormOpen(true)}
              disabled={!canCreate}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlusIcon className="w-4 h-4" />
              {t('budgets.create')}
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 p-4 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
        <div className="flex-1 min-w-[200px]">
          <input
            type="text"
            placeholder={t('budgets.searchPlaceholder')}
            value={filters.search || ''}
            onChange={(e) => setFilters({ ...filters, search: e.target.value || undefined })}
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          />
        </div>
        <div className="w-32">
          <select
            value={filters.year || ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                year: e.target.value ? parseInt(e.target.value) : undefined,
              })
            }
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          >
            <option value="">{t('budgets.allYears')}</option>
            {yearOptions.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div className="w-36">
          <select
            value={filters.status || ''}
            onChange={(e) =>
              setFilters({
                ...filters,
                status: (e.target.value as BudgetStatus) || undefined,
              })
            }
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          >
            <option value="">{t('budgets.allStatuses')}</option>
            <option value="draft">{t('budgets.draft')}</option>
            <option value="validated">{t('budgets.validated')}</option>
          </select>
        </div>
        <div className="w-48">
          <select
            value={filters.accountId || ''}
            onChange={(e) =>
              setFilters({ ...filters, accountId: e.target.value || undefined })
            }
            className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100"
          >
            <option value="">{t('budgets.allAccounts')}</option>
            {activeAccounts.map((account) => (
              <option key={account.id} value={account.id}>
                {account.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center justify-between">
          <p className="text-red-700 dark:text-red-400">{error}</p>
          <button
            onClick={clearError}
            className="text-red-500 hover:text-red-700"
          >
            <XIcon className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-violet-200 border-t-violet-600 rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && budgets.length === 0 && (
        <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700">
          <DocumentIcon className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
            {t('budgets.empty')}
          </h3>
          <p className="text-slate-500 dark:text-slate-400 mb-4">
            {t('budgets.emptyDescription')}
          </p>
          {canCreate && canManageBudgets && (
            <button
              onClick={() => setIsFormOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              {t('budgets.createFirst')}
            </button>
          )}
        </div>
      )}

      {/* Budgets grid */}
      {!isLoading && budgets.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {budgets.map((budget) => (
            <BudgetCard
              key={budget.id}
              budget={budget}
              onDelete={(id) => setDeleteConfirmId(id)}
              onDuplicate={(id) => setDuplicateId(id)}
              canManage={canManageBudgets}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setPage(page - 1)}
            disabled={page === 1}
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg disabled:opacity-50"
          >
            {t('common.previous')}
          </button>
          <span className="px-4 py-2 text-sm text-slate-600 dark:text-slate-400">
            {page} / {totalPages}
          </span>
          <button
            onClick={() => setPage(page + 1)}
            disabled={page === totalPages}
            className="px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg disabled:opacity-50"
          >
            {t('common.next')}
          </button>
        </div>
      )}

      {/* Create Form Modal */}
      <BudgetFormModal
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        onSubmit={handleCreate}
        accounts={activeAccounts}
        statements={statements}
        existingBudgets={budgets}
        isSubmitting={isSubmitting}
      />

      {/* Duplicate Modal */}
      {duplicateId && duplicateBudgetData && (
        <BudgetDuplicateModal
          isOpen={!!duplicateId}
          onClose={() => setDuplicateId(null)}
          onSubmit={handleDuplicate}
          sourceBudgetName={duplicateBudgetData.name}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setDeleteConfirmId(null)}
          />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl p-6 max-w-sm mx-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {t('budgets.deleteConfirmTitle')}
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              {t('budgets.deleteConfirmMessage')}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirmId(null)}
                className="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleDelete(deleteConfirmId)}
                disabled={isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const PlusIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const XIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M6 18L18 6M6 6l12 12"
    />
  </svg>
);

const DocumentIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);

export default BudgetsPage;
